import { getLocalStorage, saveLocalStorage } from "@elit-hair/elit-tools";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { type AudioDevice, type AudioDeviceListDiff } from "@/features/audio";
import {
    type AudioDeviceAutoSwitch,
    type AudioDeviceKind,
    isAudioDeviceKind,
} from "@/utils/audio-device-storage.utils";

const DEFAULT_UNNAMED_DEVICE_LABEL = "Unnamed device";

// Keep a bounded history so the log stays useful without growing localStorage unbounded.
export const MAX_AUDIO_DEVICE_EVENTS = 300;

export type AudioDeviceEventType = "connected" | "disconnected" | "selected-disconnected" | "auto-switch" | "selected";

export const AUDIO_DEVICE_EVENT_TYPES: AudioDeviceEventType[] = [
    "connected",
    "disconnected",
    "selected-disconnected",
    "auto-switch",
    "selected",
];

export function isAudioDeviceEventType(value: unknown): value is AudioDeviceEventType {
    return typeof value === "string" && AUDIO_DEVICE_EVENT_TYPES.some((type) => type === value);
}

export type AudioDeviceEvent = {
    /** ISO timestamp of when the event was recorded. */
    at: string;
    type: AudioDeviceEventType;
    kind: AudioDeviceKind;
    deviceId: string;
    label: string;
    /** Set for auto-switches: the device that was replaced. */
    previousDeviceId?: string | null;
    previousLabel?: string | null;
    /** Whether the affected device was the user's currently selected one. */
    wasSelected?: boolean;
};

function getSelectedDeviceIds(): { inputId: string | null; outputId: string | null } {
    const inputId = getLocalStorage<string | null>(LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.INPUT_DEVICE.key, null);
    const outputId = getLocalStorage<string | null>(LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.OUTPUT_DEVICE.key, null);

    return {
        inputId: inputId?.trim() || null,
        outputId: outputId?.trim() || null,
    };
}

export function getAudioDeviceEventLog(): AudioDeviceEvent[] {
    const stored = getLocalStorage<AudioDeviceEvent[]>(LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.EVENT_LOG.key, []);
    return Array.isArray(stored) ? stored : [];
}

export function appendAudioDeviceEvents(events: AudioDeviceEvent[]): void {
    if (events.length === 0) {
        return;
    }

    try {
        const next = [...getAudioDeviceEventLog(), ...events].slice(-MAX_AUDIO_DEVICE_EVENTS);
        saveLocalStorage(LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.EVENT_LOG.key, next);
    } catch (error) {
        console.error("Failed to persist audio device events:", error);
    }
}

export function clearAudioDeviceEventLog(): void {
    saveLocalStorage(LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.EVENT_LOG.key, []);
}

function resolveDeviceLabel(device: AudioDevice, unnamedLabel: string): string {
    return device.label?.trim() || unnamedLabel;
}

/**
 * Records connect/disconnect events for the diff produced on a `devicechange`.
 * Devices that were the user's current selection are flagged so reviewers can
 * quickly see when an in-use microphone or speaker was pulled out.
 */
export function logAudioDeviceListDiff(diff: AudioDeviceListDiff, options?: { unnamedLabel?: string }): void {
    if (diff.added.length === 0 && diff.removed.length === 0) {
        return;
    }

    const unnamedLabel = options?.unnamedLabel ?? DEFAULT_UNNAMED_DEVICE_LABEL;
    const { inputId, outputId } = getSelectedDeviceIds();
    const at = new Date().toISOString();
    const events: AudioDeviceEvent[] = [];

    for (const device of diff.removed) {
        if (!isAudioDeviceKind(device.kind)) {
            continue;
        }

        const wasSelected =
            (device.kind === "audioinput" && device.id === inputId) ||
            (device.kind === "audiooutput" && device.id === outputId);

        events.push({
            at,
            type: wasSelected ? "selected-disconnected" : "disconnected",
            kind: device.kind,
            deviceId: device.id,
            label: resolveDeviceLabel(device, unnamedLabel),
            wasSelected,
        });
    }

    for (const device of diff.added) {
        if (!isAudioDeviceKind(device.kind)) {
            continue;
        }

        events.push({
            at,
            type: "connected",
            kind: device.kind,
            deviceId: device.id,
            label: resolveDeviceLabel(device, unnamedLabel),
        });
    }

    appendAudioDeviceEvents(events);
}

/**
 * Records the automatic fallback that happens when a stored device disappears
 * and the app picks a replacement on its own.
 */
export function logAudioDeviceAutoSwitches(
    autoSwitches: AudioDeviceAutoSwitch[],
    options?: { unnamedLabel?: string }
): void {
    if (autoSwitches.length === 0) {
        return;
    }

    const unnamedLabel = options?.unnamedLabel ?? DEFAULT_UNNAMED_DEVICE_LABEL;
    const at = new Date().toISOString();
    const events: AudioDeviceEvent[] = autoSwitches.map((autoSwitch) => ({
        at,
        type: "auto-switch",
        kind: autoSwitch.kind,
        deviceId: autoSwitch.newDeviceId,
        label: autoSwitch.newLabel || unnamedLabel,
        previousDeviceId: autoSwitch.previousDeviceId,
        previousLabel: autoSwitch.previousLabel,
    }));

    appendAudioDeviceEvents(events);
}

/**
 * Records a manual device selection made by the user (settings, call dock, or
 * the "device required" dialog).
 */
export function logAudioDeviceSelection(
    kind: AudioDeviceKind,
    deviceId: string,
    label: string | null,
    options?: { unnamedLabel?: string }
): void {
    const unnamedLabel = options?.unnamedLabel ?? DEFAULT_UNNAMED_DEVICE_LABEL;
    appendAudioDeviceEvents([
        {
            at: new Date().toISOString(),
            type: "selected",
            kind,
            deviceId,
            label: label?.trim() || unnamedLabel,
            wasSelected: true,
        },
    ]);
}
